"use client";

import { Play, Clock, Eye, ExternalLink } from "lucide-react";
import type { Video } from "@/lib/site-data";

interface VideoCardProps {
  video: Video;
  layout?: "grid" | "featured";
}

function VideoThumbnail({ video, layout }: { video: Video; layout: "grid" | "featured" }) {
  const handlePlay = () => {
    window.open(`https://www.youtube.com/watch?v=${video.youtubeId}`, '_blank', 'noopener');
  };

  const size = layout === "featured"
    ? "p-5"
    : "scale-90 p-3 group-hover:scale-100 opacity-0 group-hover:opacity-100";

  const iconSize = layout === "featured" ? "h-8 w-8" : "h-5 w-5";

  return (
    <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-[var(--color-primary)]/15 via-[var(--color-surface-light)] to-[var(--color-accent)]/15">
      {/* Background decoration */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <Play className={`mx-auto text-[var(--color-primary)]/25 mb-2 ${layout === "featured" ? "h-16 w-16" : "h-10 w-10"}`} />
          <span className="text-xs text-[var(--color-muted)]/50 uppercase tracking-wider">
            {video.category}
          </span>
        </div>
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

      {/* Play button */}
      <button
        onClick={handlePlay}
        className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--color-primary)] text-black shadow-[0_0_30px_rgba(0,195,122,0.4)] transition-all duration-300 hover:scale-110 hover:shadow-[0_0_50px_rgba(0,195,122,0.6)] ${size}`}
        aria-label={`Play video: ${video.title}`}
      >
        <Play className={`${iconSize} fill-black`} />
      </button>

      {/* Duration badge */}
      <span className={`absolute right-3 rounded bg-black/70 px-2 py-0.5 text-xs font-medium text-white backdrop-blur-sm ${layout === "featured" ? "bottom-3" : "bottom-2"}`}>
        {video.duration}
      </span>

      {/* Category badge (grid only, top-left) */}
      {layout === "grid" && (
        <span className="absolute left-2 top-2 rounded-full bg-[var(--color-surface)]/90 px-2.5 py-0.5 text-xs font-medium text-[var(--color-primary)] backdrop-blur-sm">
          {video.category}
        </span>
      )}
      {layout === "featured" && (
        <span className="absolute left-3 top-3 rounded-full bg-[var(--color-primary)]/90 px-3 py-1 text-xs font-semibold text-black backdrop-blur-sm">
          {video.category}
        </span>
      )}
    </div>
  );
}

export function VideoCard({ video, layout = "grid" }: VideoCardProps) {
  if (layout === "featured") {
    return (
      <div className="group relative overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
        <VideoThumbnail video={video} layout="featured" />
        <div className="p-6">
          <div className="mb-3 flex items-center gap-4 text-sm text-[var(--color-muted)]">
            <span className="flex items-center gap-1.5">
              <Eye className="h-4 w-4" />
              {video.views} views
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              {video.date}
            </span>
          </div>
          <h3 className="mb-2 text-xl font-semibold text-[var(--color-foreground)] group-hover:text-[var(--color-primary)] transition-colors">
            {video.title}
          </h3>
          <p className="text-sm leading-relaxed text-[var(--color-muted)]">
            {video.description}
          </p>
        </div>
      </div>
    );
  }

  // Grid layout
  return (
    <div className="group relative overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--color-primary)]/50 hover:shadow-[0_8px_30px_rgba(0,195,122,0.15)]">
      <VideoThumbnail video={video} layout="grid" />
      <div className="p-4">
        <div className="mb-2 flex items-center gap-3 text-xs text-[var(--color-muted)]">
          <span className="flex items-center gap-1">
            <Eye className="h-3.5 w-3.5" />
            {video.views}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" />
            {video.date}
          </span>
        </div>
        <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-[var(--color-foreground)] group-hover:text-[var(--color-primary)] transition-colors">
          {video.title}
        </h3>
      </div>
    </div>
  );
}

// Lightweight embedded video player with click-to-load for performance
export function VideoEmbed({
  videoId,
  title,
}: {
  videoId: string;
  title: string;
}) {
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-gradient-to-br from-[var(--color-primary)]/15 via-[var(--color-surface-light)] to-[var(--color-accent)]/15">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <Play className="h-12 w-12 mx-auto text-[var(--color-primary)]/30 mb-2" />
          <span className="text-xs text-[var(--color-muted)]/50">{title}</span>
        </div>
      </div>
      <button
        onClick={() => window.open(`https://www.youtube.com/watch?v=${videoId}`, '_blank', 'noopener')}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--color-primary)] p-4 text-black shadow-[0_0_30px_rgba(0,195,122,0.4)] hover:scale-110 transition-all"
        aria-label={`Play video: ${title}`}
      >
        <ExternalLink className="h-5 w-5" />
      </button>
    </div>
  );
}
